import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack";
import request from "../../utils/request"; // Adjust the path as per your project structure

// Initial State
const initialState = {
  isLoading: false,
  last3Jobs: [], // Array to store the last 3 jobs
  last3JobsError: null, // Error state for last 3 jobs
  tasksData: null, // Store tasks count and monthly breakdown
  tasksDataError: null, // Error state for tasks data
  interviewPassFailData: null, // Store interview pass/fail data
  interviewPassFailDataError: null, // Error state for interview pass/fail data
  lastInterviewResults: [], // Store last interview results
  lastInterviewResultsError: null, // Error state for last interview results
  dashboardMetrics: null, // Store dashboard metrics
  dashboardMetricsError: null, // Error state for dashboard metrics
  candidateStagesCount: null, // Store candidate stages count
  candidateStagesCountError: null, // Error state for candidate stages count
  companyJobData: null, // Store company job data
  companyJobDataError: null, // Error state for company job data
};

// Async Thunk to fetch last 3 jobs
export const getLast3JobsOfCompany = createAsyncThunk(
  "dashboard/getLast3JobsOfCompany",
  async (company_id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-last-3-jobs-of-company",
        method: "POST",
        data: { company_id },
      });

      return response.data?.last3Jobs || [];
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async Thunk to fetch tasks count by company and year
export const getTasksCountByCompanyAndYear = createAsyncThunk(
  "dashboard/getTasksCountByCompanyAndYear",
  async ({ company_id, year }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-tasks-counts-of-company",
        method: "POST",
        data: { company_id, year },
      });

      return response.data; // Return the fetched data
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async Thunk to fetch interview pass/fail count
export const getInterviewPassFailCount = createAsyncThunk(
  "dashboard/getInterviewPassFailCount",
  async ({ company_id, duration }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-interview-pass-fail-count",
        method: "POST",
        data: { company_id, duration },
      });

      return response.data; // Return the fetched data
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async Thunk to fetch last interview results
export const getLastInterviewResults = createAsyncThunk(
  "dashboard/getLastInterviewResults",
  async (company_id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-last-interview-results", // Update the URL as per your API endpoint
        method: "POST",
        data: { company_id },
      });

      return response.data || []; // Return the fetched data
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async Thunk to fetch dashboard metrics
export const getDashboardMetrics = createAsyncThunk(
  "dashboard/getDashboardMetrics",
  async (company_id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-dashboard-metrics", // Update the URL as per your API endpoint
        method: "POST",
        data: { company_id },
      });

      return response.data; // Return the fetched data
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async Thunk to fetch candidate stages count
export const getCandidateStagesCount = createAsyncThunk(
  "dashboard/getCandidateStagesCount",
  async (company_id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-candidate-stages-count", // Update the URL as per your API endpoint
        method: "POST",
        data: { company_id },
      });

      return response.data; // Return the fetched data
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async Thunk to fetch company job data
export const getCompanyJobData = createAsyncThunk(
  "dashboard/getCompanyJobData",
  async (company_id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/dashboard-service/get-company-job-details",
        method: "POST",
        data: { company_id },
      });

      return response.data; // Return the fetched data
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Create Slice
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // You can add other reducers here if needed
  },
  extraReducers: (builder) => {
    builder
      // Handle last 3 jobs
      .addCase(getLast3JobsOfCompany.pending, (state) => {
        state.isLoading = true;
        state.last3JobsError = null;
      })
      .addCase(getLast3JobsOfCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.last3Jobs = action.payload;
      })
      .addCase(getLast3JobsOfCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.last3JobsError = action.payload || action.error.message;
      })

      // Handle tasks data
      .addCase(getTasksCountByCompanyAndYear.pending, (state) => {
        state.isLoading = true;
        state.tasksDataError = null;
      })
      .addCase(getTasksCountByCompanyAndYear.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasksData = action.payload;
      })
      .addCase(getTasksCountByCompanyAndYear.rejected, (state, action) => {
        state.isLoading = false;
        state.tasksDataError = action.payload || action.error.message;
      })

      // Handle interview pass/fail data
      .addCase(getInterviewPassFailCount.pending, (state) => {
        state.isLoading = true;
        state.interviewPassFailDataError = null;
      })
      .addCase(getInterviewPassFailCount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interviewPassFailData = action.payload;
      })
      .addCase(getInterviewPassFailCount.rejected, (state, action) => {
        state.isLoading = false;
        state.interviewPassFailDataError =
          action.payload || action.error.message;
      })

      // Handle last interview results
      .addCase(getLastInterviewResults.pending, (state) => {
        state.isLoading = true;
        state.lastInterviewResultsError = null;
      })
      .addCase(getLastInterviewResults.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastInterviewResults = action.payload;
      })
      .addCase(getLastInterviewResults.rejected, (state, action) => {
        state.isLoading = false;
        state.lastInterviewResultsError =
          action.payload || action.error.message;
      })

      // Handle dashboard metrics
      .addCase(getDashboardMetrics.pending, (state) => {
        state.isLoading = true;
        state.dashboardMetricsError = null;
      })
      .addCase(getDashboardMetrics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardMetrics = action.payload;
      })
      .addCase(getDashboardMetrics.rejected, (state, action) => {
        state.isLoading = false;
        state.dashboardMetricsError = action.payload || action.error.message;
      })

      // Handle candidate stages count
      .addCase(getCandidateStagesCount.pending, (state) => {
        state.isLoading = true;
        state.candidateStagesCountError = null;
      })
      .addCase(getCandidateStagesCount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidateStagesCount = action.payload;
      })
      .addCase(getCandidateStagesCount.rejected, (state, action) => {
        state.isLoading = false;
        state.candidateStagesCountError =
          action.payload || action.error.message;
      })

      // Handle company job data
      .addCase(getCompanyJobData.pending, (state) => {
        state.isLoading = true;
        state.companyJobDataError = null;
      })
      .addCase(getCompanyJobData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companyJobData = action.payload;
      })
      .addCase(getCompanyJobData.rejected, (state, action) => {
        state.isLoading = false;
        state.companyJobDataError = action.payload || action.error.message;
      });
  },
});

// Export Actions (if any)
export const {} = dashboardSlice.actions;

// Export Selectors
export const selectLast3Jobs = (state) => state.dashboard.last3Jobs;
export const selectLast3JobsError = (state) => state.dashboard.last3JobsError;
export const selectTasksData = (state) => state.dashboard.tasksData;
export const selectTasksDataError = (state) => state.dashboard.tasksDataError;
export const selectInterviewPassFailData = (state) =>
  state.dashboard.interviewPassFailData;
export const selectInterviewPassFailDataError = (state) =>
  state.dashboard.interviewPassFailDataError;
export const selectLastInterviewResults = (state) =>
  state.dashboard.lastInterviewResults;
export const selectLastInterviewResultsError = (state) =>
  state.dashboard.lastInterviewResultsError;
export const selectDashboardMetrics = (state) =>
  state.dashboard.dashboardMetrics;
export const selectDashboardMetricsError = (state) =>
  state.dashboard.dashboardMetricsError;
export const selectCandidateStagesCount = (state) =>
  state.dashboard.candidateStagesCount;
export const selectCandidateStagesCountError = (state) =>
  state.dashboard.candidateStagesCountError;
export const selectCompanyJobData = (state) => state.dashboard.companyJobData;
export const selectCompanyJobDataError = (state) =>
  state.dashboard.companyJobDataError;
export const selectLoading = (state) => state.dashboard.isLoading;

// Export Reducer
export default dashboardSlice.reducer;
